import { Calendar as CalendarIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useRef } from "react";
import { de, enUS, ru, sk, uk } from "react-day-picker/locale";
import type { Entries } from "type-fest";
import { z } from "zod";

import { withoutSeconds } from "../date";
import type { LanguageCode } from "../i18n";
import { formatDateTime, Tk, useTranslation2 } from "../i18n";

import { Button } from "./button";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn, selectAllText } from "./utils";

import { combine } from "~/shared/fp";
import { capitalize, COLON } from "~/shared/str";

const LOCALE_BY_LANGUAGE: Record<string, NonNullable<ComponentProps<typeof Calendar>["locale"]>> = {
  en: enUS,
  uk,
  de,
  sk,
  ru,
} satisfies Record<LanguageCode, NonNullable<ComponentProps<typeof Calendar>["locale"]>>;

type Props = ComponentProps<typeof Popover> & {
  value?: Date | undefined;
  onChange?: ((value?: Date) => void) | undefined;
  triggerProps?:
    | (Omit<ComponentProps<typeof PopoverTrigger>, "children"> & {
        children: ReactNode | ((button: ReactNode) => ReactNode);
      })
    | undefined;
} & Pick<ComponentProps<typeof Calendar>, "disabled">;

function DateTimePicker({ value, onChange, triggerProps, disabled, ...props }: Props) {
  const {
    translation: {
      i18n: { language },
    },
  } = useTranslation2();

  const timeComponentInputsRef = useRef(
    new Array<HTMLInputElement | null>(Object.keys(TimeComponentName).length).fill(null),
  );

  const {
    asChild: triggerAsChild = true,
    children: triggerChildren,
    ...triggerProps_
  } = triggerProps ?? {};

  const normalizedValue = normalizeDate(value);

  const handleChange = (value?: Date) => {
    onChange?.(normalizeDate(value));
  };

  return (
    <Popover {...props}>
      <PopoverTrigger asChild={triggerAsChild} {...triggerProps_}>
        {typeof triggerChildren === "function"
          ? triggerChildren(<TriggerButton date={normalizedValue} />)
          : (triggerChildren ?? <TriggerButton date={normalizedValue} />)}
      </PopoverTrigger>
      <PopoverContent className="flex w-auto flex-col gap-2">
        <Calendar
          mode="single"
          locale={LOCALE_BY_LANGUAGE[language]}
          {...(normalizedValue && { defaultMonth: normalizedValue })}
          selected={normalizedValue}
          onSelect={(selected) => {
            if (!selected) {
              handleChange(selected);
              return;
            }

            const newValue = new Date(normalizedValue ?? Date.now());
            newValue.setFullYear(selected.getFullYear(), selected.getMonth(), selected.getDate());
            handleChange(newValue);
          }}
          disabled={disabled}
          className="p-0"
        />
        <div className="relative flex gap-4">
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg font-bold">
            {COLON}
          </span>
          {(Object.entries(TIME_COMPONENT_METADATA) as Entries<typeof TIME_COMPONENT_METADATA>).map(
            ([name, timeComponentMetadata], index) => {
              const { get, set } = Object.fromEntries(
                Object.values(DateMethodPrefix).map((prefix) => [
                  prefix,
                  getMethodName(prefix, name),
                ]),
              ) as {
                [DateMethodPrefix.get]: ReturnType<
                  typeof getMethodName<typeof DateMethodPrefix.get, typeof name>
                >;
                [DateMethodPrefix.set]: ReturnType<
                  typeof getMethodName<typeof DateMethodPrefix.set, typeof name>
                >;
              };

              const { minimumValue, maximumValue } = timeComponentMetadata;

              return (
                <Input
                  key={name}
                  ref={(instance) => {
                    timeComponentInputsRef.current[index] = instance;
                  }}
                  min={minimumValue}
                  max={maximumValue}
                  type="number"
                  value={normalizedValue?.[get]() ?? 0}
                  onChange={(event) => {
                    const componentValue = event.target.valueAsNumber;
                    if (isNaN(componentValue)) return;

                    const newValue = new Date(normalizedValue ?? Date.now());
                    newValue[set](componentValue);
                    handleChange(newValue);
                  }}
                  onKeyDown={combine([
                    (event) => {
                      const componentValue = normalizedValue?.[get]();
                      if (typeof componentValue !== "number") return;

                      for (const [keyName, { when, then }] of Object.entries(
                        KEY_EVENT_METADATA,
                      ) as Entries<typeof KEY_EVENT_METADATA>) {
                        if (
                          event.key === keyName &&
                          componentValue === timeComponentMetadata[when]
                        ) {
                          const newValue = new Date(normalizedValue ?? Date.now());
                          newValue[set](timeComponentMetadata[then]);
                          handleChange(newValue);
                          event.preventDefault();
                        }
                      }
                    },
                    (event) => {
                      for (const { keyName, checkIndex, siblingMethodName } of [
                        {
                          keyName: "ArrowLeft",
                          checkIndex: (value: number) => value > 0,
                          siblingMethodName: "previousElementSibling",
                        },
                        {
                          keyName: "ArrowRight",
                          checkIndex: (value: number) => value < 2,
                          siblingMethodName: "nextElementSibling",
                        },
                      ] as const) {
                        if (event.key === keyName && checkIndex(index)) {
                          const sibling = (event.target as HTMLInputElement)[siblingMethodName];
                          if (sibling instanceof HTMLInputElement) {
                            sibling.focus();
                            event.preventDefault();
                          }
                        }
                      }
                    },
                  ])}
                  onFocus={selectAllText}
                  className="input-without-arrows w-1/2 text-center"
                />
              );
            },
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const zTimeComponentName = z.enum(["hour", "minute"]);
const TimeComponentName = zTimeComponentName.Enum;
type TimeComponentName = z.infer<typeof zTimeComponentName>;

type TimeComponentMetadata = {
  name: TimeComponentName;
  minimumValue: number;
  maximumValue: number;
};

const TIME_COMPONENT_METADATA: Record<TimeComponentName, Omit<TimeComponentMetadata, "name">> = {
  hour: {
    minimumValue: 0,
    maximumValue: 23,
  },
  minute: {
    minimumValue: 0,
    maximumValue: 59,
  },
};

const zDateMethodPrefix = z.enum(["get", "set"]);
const DateMethodPrefix = zDateMethodPrefix.Enum;
type DateMethodPrefix = z.infer<typeof zDateMethodPrefix>;

function getMethodName<Prefix extends DateMethodPrefix, Name extends TimeComponentName>(
  prefix: Prefix,
  name: Name,
) {
  return `${prefix}${capitalize(name)}s` as const;
}

const zKeyName = z.enum(["ArrowUp", "ArrowDown"]);
const KeyName = zKeyName.Enum;
type KeyName = z.infer<typeof zKeyName>;

type KeyEventMetadata = {
  name: KeyName;
  when: Extract<keyof TimeComponentMetadata, "minimumValue" | "maximumValue">;
  then: Extract<keyof TimeComponentMetadata, "minimumValue" | "maximumValue">;
};

const KEY_EVENT_METADATA: Record<KeyName, Omit<KeyEventMetadata, "name">> = {
  ArrowUp: {
    when: "maximumValue",
    then: "minimumValue",
  },
  ArrowDown: {
    when: "minimumValue",
    then: "maximumValue",
  },
};

function normalizeDate(value?: Date) {
  return value ? withoutSeconds(value) : value;
}

type TriggerButtonProps = ComponentProps<typeof Button> & {
  date?: Date | undefined;
};

function TriggerButton({ date, variant = "outline", className, ...props }: TriggerButtonProps) {
  const {
    translation: {
      i18n: { language },
    },
    postproc: { tc },
  } = useTranslation2();

  return (
    <Button
      variant={variant}
      data-empty={!date}
      className={cn(
        "data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal",
        className,
      )}
      {...props}
    >
      <CalendarIcon />
      {date ? formatDateTime(language, date) : <span>{tc(Tk.PICK_A_DATE)}</span>}
    </Button>
  );
}

export { DateTimePicker };
